/* ============================================================
   Bibloo – eigene Skripte (FSR-Media)
   ============================================================ */

/* Rabattcode im Warenkorb einlösen (snippets/cart-discount-code.liquid) */
class CartDiscountCode extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.cart-discount-code__input');
    this.message = this.querySelector('.cart-discount-code__message');
    this.submitButton = this.querySelector('.cart-discount-code__submit');

    this.submitButton.addEventListener('click', () => this.apply());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.apply();
      }
    });
    this.querySelectorAll('[data-remove-code]').forEach((button) => {
      button.addEventListener('click', () => this.update(this.currentCodes().filter((code) => code !== button.dataset.removeCode)));
    });
  }

  currentCodes() {
    return [...this.querySelectorAll('[data-remove-code]')].map((button) => button.dataset.removeCode);
  }

  apply() {
    const code = this.input.value.trim();
    if (!code) {
      this.input.focus();
      return;
    }
    const codes = this.currentCodes().filter((existing) => existing.toLowerCase() !== code.toLowerCase());
    this.update([...codes, code], code);
  }

  async update(codes, newCode) {
    this.setMessage('');
    this.toggleBusy(true);

    try {
      const cart = await this.sendCodes(codes);

      if (newCode) {
        const entry = (cart.discount_codes || []).find((item) => item.code.toLowerCase() === newCode.toLowerCase());

        if (!entry || !entry.applicable) {
          // Ungültigen Code wieder entfernen, damit er nicht am Warenkorb hängen bleibt
          await this.sendCodes(codes.filter((code) => code !== newCode));
          this.setMessage(this.dataset.msgInvalid || 'Discount code invalid.');
          this.toggleBusy(false);
          return;
        }
      }

      if (this.getAttribute('context') === 'page') {
        window.location.reload();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      this.setMessage(this.dataset.msgError || 'Please try again.');
      this.toggleBusy(false);
    }
  }

  async sendCodes(codes) {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    });

    if (!response.ok) {
      throw new Error(`cart/update.js ${response.status}`);
    }

    return response.json();
  }

  setMessage(text) {
    this.message.textContent = text;
    this.message.hidden = !text;
  }

  toggleBusy(busy) {
    this.submitButton.disabled = busy;
    this.input.disabled = busy;
  }
}

if (!window.customElements.get('cart-discount-code')) {
  window.customElements.define('cart-discount-code', CartDiscountCode);
}

/* Empfehlungen im Cart-Drawer: erst "Ergänzende Produkte", dann "Zugehörige Produkte" (sections/cart-drawer.liquid) */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.childElementCount > 0 || this.loading) {
      return;
    }
    this.load();
  }

  async load() {
    this.loading = true;
    const section = this.closest('.shopify-section');
    const sectionId = section ? section.id.replace(/^shopify-section-/, '') : 'cart-drawer';
    const intents = (this.getAttribute('intents') || 'complementary,related').split(',').map((intent) => intent.trim());
    const product = this.getAttribute('product');
    const limit = this.getAttribute('limit') || 4;

    try {
      for (const intent of intents) {
        const url = `${window.Shopify.routes.root}recommendations/products?product_id=${product}&limit=${limit}&section_id=${sectionId}&intent=${intent}`;
        const response = await fetch(url);
        if (!response.ok) {
          continue;
        }
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const element = doc.querySelector('cart-recommendations');

        if (element && element.childElementCount > 0) {
          this.replaceChildren(...document.importNode(element, true).childNodes);
          this.hidden = false;
          this.loading = false;
          return;
        }
      }
    } catch (error) {
      // Empfehlungen sind optional – bei Fehlern still ausblenden
    }

    this.loading = false;
    this.remove();
  }
}

if (!window.customElements.get('cart-recommendations')) {
  window.customElements.define('cart-recommendations', CartRecommendations);
}

/* Versandkosten in Fremdwährung: Umrechnung mit dem Shopify-Marktkurs (snippets/cart-shipping-info.liquid) */
class CartShippingConvert extends HTMLElement {
  connectedCallback() {
    const currency = window.Shopify && window.Shopify.currency;
    const rate = currency ? parseFloat(currency.rate) : NaN;

    if (!currency || !rate || Number.isNaN(rate)) {
      this.remove();
      return;
    }

    const format = new Intl.NumberFormat(window.Shopify.locale || 'de', { style: 'currency', currency: currency.active });
    const cost = Math.round(parseInt(this.dataset.cost, 10) * rate) / 100;
    const free = this.dataset.free ? Math.round(parseInt(this.dataset.free, 10) * rate) / 100 : null;
    const total = parseInt(this.dataset.total, 10) / 100;
    const country = this.dataset.country;
    const taxNote = this.dataset.taxesIncluded === 'true' ? ` · ${this.dataset.textInclTax}` : '';
    const fill = (text, values) => Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(values[key]), text);
    const escape = (text) => text.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let html = '';

    if (free !== null) {
      html += `<free-shipping-bar class="free-shipping-bar" threshold="${free}" total-price="${Math.round(total * 100)}" reached-message="${escape(this.dataset.reached)}" unreached-message="${escape(this.dataset.unreached)}"><span class="text-subdued">&nbsp;</span></free-shipping-bar>`;
    }

    if (free !== null && total >= free) {
      html += `<p class="cart-shipping-info__costs text-xs">${fill(this.dataset.textFreeShippingTo, { country })}${taxNote}</p>`;
    } else {
      const freeText = free !== null ? ` · ${fill(this.dataset.textFreeFrom, { amount: format.format(free) })}` : '';
      html += `<p class="cart-shipping-info__costs text-xs">${fill(this.dataset.textShippingTo, { country, cost: format.format(cost) })}${freeText}${taxNote}</p>`;
    }

    this.innerHTML = html;
  }
}

if (!window.customElements.get('cart-shipping-convert')) {
  window.customElements.define('cart-shipping-convert', CartShippingConvert);
}
